import { useState } from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../shared/auth/useAuth';
import { ClienteSelect } from '../../../shared/components/ClienteSelect';
import { downloadAsPdf } from '../../../shared/reports/downloadAsPdf';
import { downloadAsXlsx } from '../../../shared/reports/downloadAsXlsx';
import {
  buildFilename,
  formatDate,
  formatHours,
  formatMoney,
  formatPeriodo,
} from '../../../shared/reports/formatters';
import { useGerarBilling } from '../hooks';
import { RelatorioPageShell } from '../RelatorioPageShell';
import { MesAnoFilter } from '../components/MesAnoFilter';
import type { BillingDto } from '../types';

export function ExtratoFaturamentoPage(): JSX.Element {
  const now = new Date();
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());
  const mutation = useGerarBilling();
  const { user } = useAuth();
  const usuario = user?.nome ?? '';

  const result = mutation.data;
  const canGenerate = clienteId !== null && mes >= 1 && mes <= 12 && ano >= 2000;

  function gerar(): void {
    if (!canGenerate || clienteId === null) return;
    mutation.mutate({ clienteId, ano, mes });
  }

  function exportPdf(): void {
    if (!result) return;
    const periodo = formatPeriodo(result.cabecalho.mes, result.cabecalho.ano);
    downloadAsPdf(
      buildFilename('extrato-faturamento', result.cabecalho.clienteNome, periodo),
      {
        title: 'Extrato de Faturamento',
        subtitle: `${result.cabecalho.clienteNome} — ${periodo}`,
        usuario,
      },
      ({ doc, contentStartY }) => {
        autoTable(doc, {
          startY: contentStartY,
          head: [['Data', 'Projeto', 'Colaborador', 'Horas', 'Valor/h', 'Valor']],
          body: result.linhas.map((l) => [
            formatDate(l.data),
            l.projetoNome,
            l.colaboradorNome,
            l.horas,
            formatMoney(l.valorHora),
            formatMoney(l.valor),
          ]),
          foot: [
            [
              { content: 'Total', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
              result.totais.totalHoras,
              '',
              formatMoney(result.totais.totalValor),
            ],
          ],
          styles: { fontSize: 8 },
          headStyles: { fillColor: [30, 60, 100] },
          footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
        });

        if (result.totais.porProjeto.length > 0) {
          autoTable(doc, {
            head: [['Resumo por projeto', 'Horas', 'Valor']],
            body: result.totais.porProjeto.map((p) => [
              p.projetoNome,
              p.horas,
              formatMoney(p.valor),
            ]),
            styles: { fontSize: 9 },
            headStyles: { fillColor: [80, 80, 80] },
          });
        }
      },
    );
  }

  function exportXlsx(): void {
    if (!result) return;
    const periodo = formatPeriodo(result.cabecalho.mes, result.cabecalho.ano);
    downloadAsXlsx(buildFilename('extrato-faturamento', result.cabecalho.clienteNome, periodo), [
      {
        name: 'Apontamentos',
        rows: result.linhas.map((l) => ({
          Data: l.data,
          Projeto: l.projetoNome,
          Colaborador: l.colaboradorNome,
          Horas: l.horas,
          ValorHora: l.valorHora,
          Valor: l.valor,
        })),
        columns: [
          { key: 'Data', header: 'Data', format: 'date', width: 12 },
          { key: 'Projeto', header: 'Projeto', width: 28 },
          { key: 'Colaborador', header: 'Colaborador', width: 28 },
          { key: 'Horas', header: 'Horas', width: 10 },
          { key: 'ValorHora', header: 'Valor/h', format: 'money', width: 14 },
          { key: 'Valor', header: 'Valor', format: 'money', width: 14 },
        ],
      },
      {
        name: 'Por projeto',
        rows: result.totais.porProjeto.map((p) => ({
          Projeto: p.projetoNome,
          Horas: p.horas,
          Valor: p.valor,
        })),
        columns: [
          { key: 'Projeto', header: 'Projeto', width: 32 },
          { key: 'Horas', header: 'Horas', width: 10 },
          { key: 'Valor', header: 'Valor', format: 'money', width: 14 },
        ],
      },
    ]);
  }

  return (
    <RelatorioPageShell
      title="Extrato de Faturamento"
      subtitle="Apontamentos por projeto × colaborador × dia, valorizados pela taxa vigente."
      filters={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ minWidth: 260, flex: 1 }}>
            <ClienteSelect
              value={clienteId}
              onChange={(id) => setClienteId(id)}
              label="Cliente"
              disabled={mutation.isPending}
            />
          </Box>
          <MesAnoFilter
            mes={mes}
            ano={ano}
            onChange={({ mes: m, ano: a }) => {
              setMes(m);
              setAno(a);
            }}
            disabled={mutation.isPending}
          />
        </Stack>
      }
      canGenerate={canGenerate}
      onGenerate={gerar}
      generating={mutation.isPending}
      hasResult={!!result}
      error={mutation.isError ? mutation.error : null}
      emptyMessage="Escolha cliente e período e clique em Gerar."
      onExportPdf={exportPdf}
      onExportXlsx={exportXlsx}
      preview={result ? <Preview result={result} /> : null}
    />
  );
}

function Preview({ result }: { result: BillingDto }): JSX.Element {
  const periodo = formatPeriodo(result.cabecalho.mes, result.cabecalho.ano);
  return (
    <>
      <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap">
        <ResumoItem label="Cliente" value={result.cabecalho.clienteNome} />
        <ResumoItem label="Período" value={periodo} />
        <ResumoItem label="Horas totais" value={formatHours(result.totais.totalHoras)} />
        <ResumoItem label="Valor total" value={formatMoney(result.totais.totalValor)} />
      </Stack>

      {result.linhas.length === 0 ? (
        <Typography color="text.secondary">Nenhum apontamento neste período.</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Projeto</TableCell>
                <TableCell>Colaborador</TableCell>
                <TableCell align="right">Horas</TableCell>
                <TableCell align="right">Valor/h</TableCell>
                <TableCell align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.linhas.map((l, i) => (
                <TableRow key={`${l.data}-${l.projetoCodigo}-${l.colaboradorCodigo}-${i}`}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{formatDate(l.data)}</TableCell>
                  <TableCell>{l.projetoNome}</TableCell>
                  <TableCell>{l.colaboradorNome}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                    {l.horas}
                  </TableCell>
                  <TableCell align="right">{formatMoney(l.valorHora)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {formatMoney(l.valor)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {result.totais.porProjeto.length > 0 && (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom>
            Resumo por projeto
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Projeto</TableCell>
                  <TableCell align="right">Horas</TableCell>
                  <TableCell align="right">Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.totais.porProjeto.map((p) => (
                  <TableRow key={p.projetoCodigo}>
                    <TableCell>{p.projetoNome}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {p.horas}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {formatMoney(p.valor)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </>
  );
}

function ResumoItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack spacing={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Stack>
  );
}
